import { useEffect, useState } from "react";

import controlPlaneApi from "../axiosControlPlane";

// Module-level cache so every consumer shares a single /feature-flags request
// per session instead of refetching on each mount.
let cachedFlags = null;
let inFlightRequest = null;

export const parseFeatureFlags = (data) =>
  data && typeof data === "object" && !Array.isArray(data) ? data : {};

// Test/logout hook: drops the cache so the next call requests again.
export const resetFeatureFlagsCache = () => {
  cachedFlags = null;
  inFlightRequest = null;
};

// Feature flags are control plane: always read from the env backend, never
// from the backend the session was routed to (see axiosControlPlane.js).
const requestFeatureFlags = () =>
  controlPlaneApi.get("/feature-flags").then((response) => parseFeatureFlags(response?.data));

// `force: true` (boolean only) bypasses the cache, performs a NEW request and
// replaces the cache with the fresh result, so hook consumers see the same
// flags. Used by the backend-target resolver on every login/verifyToken: the
// module cache leaks across a logout without page reload, and a stale
// `mongoBackend` from a previous company must never decide the routing.
export const fetchFeatureFlags = (options) => {
  const force = Boolean(options && typeof options === "object" && options.force === true);
  if (!force && cachedFlags) return Promise.resolve(cachedFlags);
  if (force || !inFlightRequest) {
    const request = requestFeatureFlags()
      .then((flags) => {
        // Only the request that is still current may write the cache: an
        // older, slower request (previous company) completing after a newer
        // forced one must not overwrite the newer flags. Its own caller still
        // gets its own answer.
        if (inFlightRequest === request) cachedFlags = flags;
        return flags;
      })
      .catch(() => {
        // Fail closed: every flag reads as false on any error. Only the
        // request that is still current resets the shared state — a stale
        // rejection must not clobber a newer forced request. A forced
        // failure also drops the cache: stale flags from before the failure
        // must not be served as if they were fresh.
        if (inFlightRequest === request) {
          inFlightRequest = null;
          if (force) cachedFlags = null;
        }
        return {};
      });
    inFlightRequest = request;
  }
  return inFlightRequest;
};

const useFeatureFlags = () => {
  const [flags, setFlags] = useState(cachedFlags || {});
  const [loading, setLoading] = useState(!cachedFlags);

  useEffect(() => {
    let mounted = true;
    fetchFeatureFlags().then((resolvedFlags) => {
      if (mounted) {
        setFlags(resolvedFlags);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return { flags, loading };
};

export default useFeatureFlags;
